import React, { useEffect, useRef } from 'react';

import { useIntl } from 'react-intl';

import StatusInformationIcon from '@atlaskit/icon/core/status-information';
import { fg } from '@atlaskit/platform-feature-flags';

import { universalTypeFilterId } from '../../../../../common/constants/filters';
import { TypeFilterValueKey } from '../../../../../common/constants/filters/universal-type';
import SharedSalesforceDynamicMultiSelect from '../../../../../common/ui/filters/product-filters/shared-salesforce-dynamic-multi-select';
import type { FilterProps } from '../../../../../common/ui/filters/product-filters/types';
import { useSelectFilter } from '../../../../../controllers/filters/base-select-filter';
import { useFiltersDiscoverability2Enabled } from '../../../../../controllers/store/bootstrap';
import { filter as caseStatusFilter } from '../../../../../controllers/store/search/filters/case-status';

import { i18n } from './messages';

const CASE_STATUS_VISIBLE_TYPES = [TypeFilterValueKey.Case] as const;

const CaseStatusFilter = (props: FilterProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const isFiltersDiscoverability2Enabled = useFiltersDiscoverability2Enabled();
	const [typeFilterData] = useSelectFilter({ filterId: universalTypeFilterId });
	const [filterData, { loadInitialOptions, setOption, clearOptions }] = useSelectFilter({
		filterId: caseStatusFilter.id,
	});
	const isCaseStatusFilterEnabled = fg('rovo-fps-3p-ux-sf-case-status-filter');
	const hasLoadedInitialOptions = useRef(false);

	useEffect(() => {
		if (!hasLoadedInitialOptions.current && isCaseStatusFilterEnabled) {
			hasLoadedInitialOptions.current = true;
			loadInitialOptions();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isCaseStatusFilterEnabled]);

	const selectedTypes = typeFilterData?.selectedOptions.map((option) => option.value) ?? [];
	const isAllTypeSelected = selectedTypes.length === 0;
	const isCaseTypeSelected =
		selectedTypes.length > 0 &&
		selectedTypes.every((type) => (CASE_STATUS_VISIBLE_TYPES as readonly string[]).includes(type));
	const shouldShowCaseStatusFilter = isAllTypeSelected || isCaseTypeSelected;

	useEffect(() => {
		if (isCaseStatusFilterEnabled && !shouldShowCaseStatusFilter) {
			clearOptions();
		}
	}, [clearOptions, isCaseStatusFilterEnabled, shouldShowCaseStatusFilter]);

	if (!filterData || !isCaseStatusFilterEnabled || !shouldShowCaseStatusFilter) {
		return null;
	}

	const { isLoading, suggestedOptions, selectedOptions } = filterData;

	return (
		<SharedSalesforceDynamicMultiSelect
			{...props}
			filterId={caseStatusFilter.id}
			label={formatMessage(i18n.caseStatusFilterLabel)}
			noOptionsMessage={formatMessage(i18n.caseStatusFilterNoOptions)}
			iconBefore={<StatusInformationIcon label="" />}
			isLoading={isLoading}
			selectedOptions={selectedOptions}
			suggestedOptions={suggestedOptions}
			isFiltersDiscoverability2Enabled={isFiltersDiscoverability2Enabled}
			loadInitialOptions={loadInitialOptions}
			setOption={(option) => setOption(option)}
			clearOptions={clearOptions}
		/>
	);
};

export default CaseStatusFilter;
